/**
 * MenuBar — eQMS top row.
 *
 * Four menus, each a real medtech-eQMS surface (no desktop-app skeuomorphism):
 *   File     — new/open product, ingest (document / connector), generate deliverables
 *              (DoC / V&V report / traceability matrix / audit package), sign out
 *   Insert   — global new-record hub (product / requirement / standard / session / doc)
 *   Tools    — the QMS record lifecycle: document control, change control
 *              (NCR / CAPA / preventive / post-market), risk register, audit trail
 *   Automate — AI / automation entry points: e-pilot, workflows, report gen
 *   View     — theme + view-state toggles
 *   Help     — help center / tour / shortcuts / about
 *
 * Deep-link items call onSectionChange(section, tab, subTab); the left nav
 * remains the primary section switcher — these are task entry points that
 * carry a verb (generate, submit, diagnose) the sidebar doesn't.
 *
 * The old Edit and Window menus were removed (browser-native clipboard +
 * permanently-stubbed multi-window). Handlers are passed down from App.js.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import FileMenu from './menus/FileMenu';
import InsertMenu from './menus/InsertMenu';
import ToolsMenu from './menus/ToolsMenu';
import AutomateMenu from './menus/AutomateMenu';
import ViewMenu from './menus/ViewMenu';
import HelpMenu from './menus/HelpMenu';

const LABELS = [
  { id: 'file',     label: 'File',     hotkey: 'f' },
  { id: 'insert',   label: 'Insert',   hotkey: 'i' },
  { id: 'tools',    label: 'Tools',    hotkey: 't' },
  { id: 'automate', label: 'Automate', hotkey: 'a' },
  { id: 'view',     label: 'View',     hotkey: 'v' },
  { id: 'help',     label: 'Help',     hotkey: 'h' },
];

const MenuBar = ({
  hasProject,
  hasBranch,
  // Shared navigation
  onSectionChange,
  onVersionControl,
  // File
  onNewProject,
  onOpenProject,
  onProperties,
  onSignOut,
  // Create
  onCreate,
  // Tools
  onOpenApprovals,
  // AI
  onToggleChat,
  // View
  onRefresh,
  onToggleFullscreen,
  // Help
  onHelpCenter,
  onStartTour,
  onKeyboardShortcuts,
  onAbout,
}) => {
  const [openMenu, setOpenMenu] = useState(null);
  const anchorRefs = useRef({});

  const handleOpen = (id) => (e) => {
    anchorRefs.current[id] = e.currentTarget;
    setOpenMenu(id);
  };
  const handleClose = () => setOpenMenu(null);

  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey) return;
      const match = LABELS.find((l) => l.hotkey === e.key.toLowerCase());
      if (!match) return;
      const node = document.querySelector(`[data-menubar-trigger="${match.id}"]`);
      if (node) {
        e.preventDefault();
        anchorRefs.current[match.id] = node;
        setOpenMenu(match.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        height: 32,
        px: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {LABELS.map(({ id, label }) => (
        <Button
          key={id}
          data-menubar-trigger={id}
          onClick={handleOpen(id)}
          sx={{
            minWidth: 0,
            px: 1.25,
            py: 0.25,
            height: 28,
            fontSize: '0.8125rem',
            fontWeight: 400,
            color: 'text.primary',
            textTransform: 'none',
            borderRadius: 0,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <span>
            <span style={{ textDecoration: 'underline' }}>{label.charAt(0)}</span>
            {label.slice(1)}
          </span>
        </Button>
      ))}

      <FileMenu
        anchorEl={anchorRefs.current.file}
        open={openMenu === 'file'}
        onClose={handleClose}
        hasProject={hasProject}
        onSectionChange={onSectionChange}
        onNewProject={onNewProject}
        onOpenProject={onOpenProject}
        onProperties={onProperties}
        onSignOut={onSignOut}
      />
      <InsertMenu
        anchorEl={anchorRefs.current.insert}
        open={openMenu === 'insert'}
        onClose={handleClose}
        hasProject={hasProject}
        onCreate={onCreate}
      />
      <ToolsMenu
        anchorEl={anchorRefs.current.tools}
        open={openMenu === 'tools'}
        onClose={handleClose}
        hasProject={hasProject}
        hasBranch={hasBranch}
        onSectionChange={onSectionChange}
        onVersionControl={onVersionControl}
        onOpenApprovals={onOpenApprovals}
      />
      <AutomateMenu
        anchorEl={anchorRefs.current.automate}
        open={openMenu === 'automate'}
        onClose={handleClose}
        hasProject={hasProject}
        onSectionChange={onSectionChange}
        onToggleChat={onToggleChat}
      />
      <ViewMenu
        anchorEl={anchorRefs.current.view}
        open={openMenu === 'view'}
        onClose={handleClose}
        onRefresh={onRefresh}
        onToggleFullscreen={onToggleFullscreen}
      />
      <HelpMenu
        anchorEl={anchorRefs.current.help}
        open={openMenu === 'help'}
        onClose={handleClose}
        onHelpCenter={onHelpCenter}
        onStartTour={onStartTour}
        onKeyboardShortcuts={onKeyboardShortcuts}
        onAbout={onAbout}
      />
    </Box>
  );
};

export default MenuBar;
